import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, List } from "semantic-ui-react";
import { useFeature, useSubmitReview } from "../../hooks";
import { CommunitySelectionModal } from "../utils";

const FeatureName = "PUBLISH_BUTTON_ENABLED";

export const SubmitReviewPublishButton = ({
  draftId,
  draft,
  disabled,
}) => {
  const { enabled: publishButtonEnabled, isLoading: isFeatureLoading } =
    useFeature(FeatureName);
  const {
    loading: isSubmitting,
    validationErrors,
    submitForReview,
  } = useSubmitReview();

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCommunityModal, setShowCommunityModal] = useState(false);

  // Get the draft status - determines if we need community selection
  const draftStatus = draft?.status;
  const needsCommunitySelection = draftStatus === "draft";

  const handleClick = async () => {
    if (!draft) return;

    if (needsCommunitySelection) {
      // For "draft" status, open community selection modal
      setShowCommunityModal(true);
    } else {
      // For "draft_with_review" status, submit directly without community selection
      const result = await submitForReview(draft, draftId, null);

      if (result.success) {
        window.location.reload();
      } else if (result.validationErrors) {
        setShowErrorModal(true);
      }
    }
  };

  const handleCommunitySelect = async (communityId, communityTitle) => {
    setShowCommunityModal(false);

    const result = await submitForReview(draft, draftId, communityId);

    if (result.success) {
      window.location.reload();
    } else if (result.validationErrors) {
      setShowErrorModal(true);
    }
  };

  const isLoading = isFeatureLoading;

  if (!isLoading && !publishButtonEnabled) {
    return (
      <Button fluid secondary disabled>
        Request for publication
      </Button>
    );
  }

  const buttonText = isSubmitting
    ? "Submitting..."
    : needsCommunitySelection
      ? "Request for publication"
      : "Submit for review";

  return (
    <>
      {isLoading ? (
        <Button fluid disabled>
          Preparing...
        </Button>
      ) : (
        <Button
          fluid
          secondary
          disabled={(disabled ?? false) || isSubmitting}
          onClick={handleClick}
        >
          {buttonText}
        </Button>
      )}

      {/* Community Selection Modal - only shown for "draft" status */}
      {needsCommunitySelection && (
        <CommunitySelectionModal
          open={showCommunityModal}
          onClose={() => setShowCommunityModal(false)}
          onCommunitySelect={handleCommunitySelect}
        />
      )}

      {/* Error Modal for validation errors */}
      <Modal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        size="small"
      >
        <Modal.Header>Metadata Validation Errors</Modal.Header>
        <Modal.Content>
          <p>
            Please fix the following validation errors before submitting for
            review:
          </p>
          <List unordered>
            {validationErrors.map((error, index) => (
              <List.Item key={index}>{error}</List.Item>
            ))}
          </List>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowErrorModal(false)} primary>
            OK
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

SubmitReviewPublishButton.propTypes = {
  draftId: PropTypes.string.isRequired,
  draft: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};
